import { z } from 'zod'
import { MAX_SEGMENT_MS } from './meeting-audio'

/**
 * Realtime meetings (PLAN-085) — the `MeetingRoom` socket protocol
 * (`/ws/:projectId/meeting/:meetingId`).
 *
 * Text frames are JSON and typed here; **binary** frames are microphone
 * segments and use `meeting-audio.ts` instead. Media (camera, screen share,
 * remote audio) never travels this socket — that is RealtimeKit's SFU. What
 * moves here is: who is connected, the transcript as Whisper produces it,
 * the rolling summary, and the end of the meeting.
 *
 * Nothing here is a source of truth. Transcript rows live in the
 * ProjectStore (`meeting_transcripts`), the summary in the room DO, and the
 * notes in the DocumentStore — the socket only fans them out.
 */

export const meetingRoomStatusSchema = z.enum(['scheduled', 'live', 'ended'])
export type MeetingRoomStatus = z.infer<typeof meetingRoomStatusSchema>

/** What the control bar's transcription indicator shows. */
export const transcriptionStateSchema = z.enum(['live', 'degraded', 'paused', 'off'])
export type TranscriptionState = z.infer<typeof transcriptionStateSchema>

/**
 * Why transcription is 'paused' (board m5gc #2). 'credits' is the budget
 * gate; 'host' is the meeting host choosing that HUB should not listen. Both
 * are the same `paused` state on the wire and in the ingest policy — nothing
 * is sent, accepted or billed either way — the UI only words them apart.
 */
export const transcriptionPauseReasonSchema = z.enum(['host', 'credits'])
export type TranscriptionPauseReason = z.infer<typeof transcriptionPauseReasonSchema>

/** Why the room ended — the ended screen picks its wording from this. */
export const meetingEndReasonSchema = z.enum(['host', 'idle', 'cap', 'rtk', 'deleted'])
export type MeetingEndReason = z.infer<typeof meetingEndReasonSchema>

export const meetingParticipantSchema = z.object({
  userId: z.string(),
  name: z.string(),
  avatarUrl: z.string().nullable(),
  connected: z.boolean(),
  /** When this person raised their hand (board m5gc #12); null = down. */
  handRaisedAt: z.string().nullable(),
})
export type MeetingParticipant = z.infer<typeof meetingParticipantSchema>

export const transcriptSegmentSchema = z.object({
  seq: z.number().int(),
  userId: z.string(),
  speaker: z.string(),
  /** Milliseconds since the room started. */
  startMs: z.number().int(),
  endMs: z.number().int(),
  text: z.string(),
  /**
   * A line HUB wrote itself rather than heard — 'Transcription paused by the
   * host' and its resume. Rendered as a divider in the panel, and kept in the
   * transcript so the notes can say the gap was intentional.
   */
  marker: z.boolean().optional(),
})
export type TranscriptSegment = z.infer<typeof transcriptSegmentSchema>

export const meetingActionItemSchema = z.object({
  text: z.string(),
  owner: z.string().optional(),
})
export type MeetingActionItem = z.infer<typeof meetingActionItemSchema>

/** The rolling summary the DO keeps and rewrites into the notes document. */
export const meetingSummarySchema = z.object({
  updatedAt: z.string(),
  summary: z.string(),
  decisions: z.array(z.string()),
  actions: z.array(meetingActionItemSchema),
})
export type MeetingSummary = z.infer<typeof meetingSummarySchema>

// ── Client → server (text) ────────────────────────────────────────────────

export const meetingClientMessageSchema = z.discriminatedUnion('t', [
  /** Sent right after open; the server answers with `state`. */
  z.object({ t: z.literal('hello'), clientId: z.string().max(64) }),
  z.object({ t: z.literal('leave') }),
  /** Host only: pause or resume the whole room's transcription. */
  z.object({ t: z.literal('transcription'), action: z.enum(['pause', 'resume']) }),
  /**
   * Raise or lower a hand (board m5gc #12). `userId` names someone else's
   * hand — only the host may do that, and only to lower it.
   */
  z.object({ t: z.literal('hand'), raised: z.boolean(), userId: z.string().optional() }),
])
export type MeetingClientMessage = z.infer<typeof meetingClientMessageSchema>

// ── Server → client (text) ────────────────────────────────────────────────

/** Transcript tail replayed to a joining or reconnecting client. */
export const MEETING_STATE_SEGMENTS = 200

export const meetingServerMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('state'),
    status: meetingRoomStatusSchema,
    startedAt: z.string().nullable(),
    participants: z.array(meetingParticipantSchema),
    summary: meetingSummarySchema.nullable(),
    /** The last {@link MEETING_STATE_SEGMENTS} segments, oldest first. */
    segments: z.array(transcriptSegmentSchema),
    transcription: transcriptionStateSchema,
    pausedBy: transcriptionPauseReasonSchema.nullable(),
    /** A RealtimeKit recording of the meeting is running (board m5gc #8). */
    recording: z.boolean(),
    /** Server clock at send time, so a late joiner can place `startMs`. */
    serverNow: z.string(),
  }),
  z.object({ t: z.literal('participants'), participants: z.array(meetingParticipantSchema) }),
  z.object({ t: z.literal('transcript'), segment: transcriptSegmentSchema }),
  z.object({ t: z.literal('summary'), summary: meetingSummarySchema }),
  z.object({
    t: z.literal('transcription'),
    state: transcriptionStateSchema,
    pausedBy: transcriptionPauseReasonSchema.nullable(),
  }),
  z.object({ t: z.literal('recording'), active: z.boolean() }),
  z.object({
    t: z.literal('ended'),
    reason: meetingEndReasonSchema,
    notesDocumentId: z.string().nullable(),
  }),
  z.object({ t: z.literal('error'), message: z.string() }),
])
export type MeetingServerMessage = z.infer<typeof meetingServerMessageSchema>

// ── Socket close codes ────────────────────────────────────────────────────

/**
 * The room was never started through `meetings.join`, so there is nothing to
 * connect to. A project member must not be able to spin up an empty room for
 * an arbitrary meeting id.
 */
export const MEETING_CLOSE_NOT_STARTED = 4004

/** The meeting is over; the client shows the ended screen instead of reconnecting. */
export const MEETING_CLOSE_ENDED = 4005

// ── Ingest limits (enforced by the DO, mirrored by the browser) ───────────

/** At most one audio frame per second per connection. */
export const AUDIO_FRAMES_PER_SECOND = 1

/** Audio a single connection may push per 15 s of wall clock. */
export const AUDIO_BUDGET_MS_PER_WINDOW = 16_000
export const AUDIO_BUDGET_WINDOW_MS = 15_000

/**
 * Audio the browser holds while its socket is down, so a network blip does
 * not cost the transcript a minute of speech.
 */
export const MEETING_AUDIO_BUFFER_MS = 60_000

/**
 * Headroom the room gives a **fresh** connection on top of its own age.
 *
 * These two numbers are one decision, which is why they live together: a
 * reconnecting client drains its whole buffer in the first seconds of a new
 * connection, so anything less than the buffer here means the room refuses
 * the tail of it as `over-budget` (REVIEW-2 #3). One extra segment of slack
 * covers the one in flight when the socket dropped.
 */
export const INGEST_SLACK_MS = MEETING_AUDIO_BUFFER_MS + MAX_SEGMENT_MS
